'use client';

import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

export interface DeptCard {
  id: string;
  label: string;
  icon: string;
  status: 'green' | 'yellow' | 'red';
  route: string;
  metrics: { label: string; value: string }[];
}

const STATUS_CONFIG = {
  green:  { dot: 'bg-green-500',  label: 'Optimal',  text: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-200' },
  yellow: { dot: 'bg-yellow-500', label: 'At Risk',  text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  red:    { dot: 'bg-red-500',    label: 'Critical', text: 'text-red-600',    bg: 'bg-red-50',    border: 'border-red-200' },
};

interface Props {
  deptCards: DeptCard[];
}

export default function Row4DeptHealthMatrix({ deptCards }: Props) {
  const router = useRouter();

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold" style={{ color: '#000150' }}>
            Departmental Health Matrix
          </h2>
          <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
            Click any card to navigate to that department's dashboard
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-xs" style={{ color: '#6B7280' }}>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />Optimal</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-yellow-500 inline-block" />At Risk</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />Critical</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {deptCards.map((card) => {
          const sc = STATUS_CONFIG[card.status];
          return (
            <button
              key={card.id}
              onClick={() => router.push(card.route)}
              className="text-left rounded-xl p-5 transition-all hover:scale-[1.02] hover:shadow-lg group cursor-pointer"
              style={{
                backgroundColor: '#FFFFFF',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #F0F0F0',
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: '#F8F9FA' }}
                  >
                    <Icon name={card.icon} size={20} style={{ color: '#033494' }} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: '#000150' }}>{card.label}</p>
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border mt-1 ${sc.bg} ${sc.text} ${sc.border}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                      {sc.label}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`w-3 h-3 rounded-full ${sc.dot} shadow-sm`} />
                  <Icon name="ArrowTopRightOnSquareIcon" size={13} className="text-gray-300 group-hover:text-gray-500 transition-colors" />
                </div>
              </div>

              <div className="space-y-2.5">
                {card.metrics.map((m, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: '#6B7280' }}>{m.label}</span>
                    <span className="text-sm font-bold" style={{ color: '#000150' }}>{m.value}</span>
                  </div>
                ))}
              </div>

              <div
                className="mt-4 pt-3 flex items-center justify-between"
                style={{ borderTop: '1px solid #F3F4F6' }}
              >
                <span className="text-xs" style={{ color: '#9CA3AF' }}>View full dashboard</span>
                <Icon name="ChevronRightIcon" size={14} className="text-gray-400 group-hover:text-cyan-500 transition-colors" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
